import axios from 'axios';

export type Environment = 'testnet' | 'mainnet' | 'sandbox';

export type ClientOptions = {};

export enum MessageStatus {
  INFLIGHT = 'INFLIGHT',
  DELIVERED = 'DELIVERED',
  FAILED = 'FAILED',
}

export type Message = {
  srcUaAddress: string;
  dstUaAddress: string;
  srcChainId: number;
  dstChainId: number;
  dstTxHash?: string;
  dstTxError?: string;
  srcTxHash?: string;
  srcBlockHash?: string;
  srcBlockNumber?: string;
  srcUaNonce: number;
  status: MessageStatus;
};

const URLS: Record<Environment, string> = {
  testnet: 'https://api-testnet.layerzero-scan.com',
  mainnet: 'https://api-mainnet.layerzero-scan.com',
  sandbox: 'https://api-sandbox.layerzero-scan.com',
};

type GetMessagesBySrcTxHashResponse = {
  messages: Message[];
};

export const createClient = (env: Environment, options?: ClientOptions) => {
  const url = URLS[env];
  if (!url) throw new Error(`No endpoint for env ${env}`);

  const client = axios.create({
    baseURL: url,
  });

  return {
    async getMessagesBySrcTxHash(srcTxHash: string) {
      if (!srcTxHash) throw new Error('srcTxHash must be provided');
      const {data} = await client.get<GetMessagesBySrcTxHashResponse>(`/tx/${srcTxHash}`);
      return data;
    },
  };
};

export function getMessagesBySrcTxHash(srcChainId: number, srcTxHash: string) {
  const env = srcChainId < 10000 ? 'mainnet' : srcChainId < 20000 ? 'testnet' : 'sandbox';
  return createClient(env).getMessagesBySrcTxHash(srcTxHash);
}

type DeliveredMessage = Message & {dstTxHash: string};

export async function waitForMessageReceived(
  srcChainId: number,
  srcTxHash: string,
  pollInterval = 3000,
): Promise<DeliveredMessage> {
  const env = srcChainId < 10000 ? 'mainnet' : srcChainId < 20000 ? 'testnet' : 'sandbox';
  const client = createClient(env);
  while (true) {
    try {
      const {messages} = await client.getMessagesBySrcTxHash(srcTxHash);
      const message = messages[0];
      if (messages.length > 1) {
        return Promise.reject(new Error(`More than one message`));
      } else if (message?.status === MessageStatus.FAILED) {
        return Promise.reject(new Error(`Message failed ${message.dstTxError}`));
      } else if (message?.status === MessageStatus.DELIVERED) {
        return message as DeliveredMessage;
      }
    } catch {
      // http error
    }
    await sleep(pollInterval);
  }
}

export async function waitForAllMessagesReceived(
  srcChainId: number,
  srcTxHash: string,
  pollInterval = 3000,
  numberOfMessages?: number,
): Promise<DeliveredMessage[]> {
  const env = srcChainId < 10000 ? 'mainnet' : srcChainId < 20000 ? 'testnet' : 'sandbox';
  const client = createClient(env);
  while (true) {
    try {
      const {messages} = await client.getMessagesBySrcTxHash(srcTxHash);
      if (messages.length > 0) {
        if (messages.every((m) => m.status === MessageStatus.DELIVERED)) {
          if (numberOfMessages === undefined || numberOfMessages === messages.length) {
            return messages as DeliveredMessage[];
          }
        }
      }
    } catch {
      // http error
    }
    await sleep(pollInterval);
  }
}

const sleep = (ms = 1000) => new Promise((resolve) => setTimeout(resolve, ms));
